/**
 * React Query hooks for XCEL prelicensing.
 *
 * Keys carry the full selection (filters, account, run) and every `queryFn`
 * forwards `signal`, so a superseded response is both ignored and cancelled.
 * Runs are polled every 3 s while queued / running / delayed and stop on a
 * terminal state or unmount — progress comes from the worker, never a timer.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/features/auth';

import {
  XcelApiError,
  fetchAccountPrograms,
  fetchAccountProgress,
  fetchHierarchyOptions,
  fetchMappingCounts,
  fetchMappings,
  fetchRun,
  fetchRunActivity,
  fetchRuns,
  fetchSettings,
  fetchStatus,
  fetchXcelAccess,
  patchSettings,
  postDecision,
  queueProbe,
  queueRun,
  requestAccountRefresh,
} from '../services/xcel-prelicensing-service';
import {
  ACTIVE_RUN_STATES,
  type Decision,
  type MappingFilters,
  type QueueableRunType,
  type SettingsPatch,
  type XcelRun,
} from '../types';

const KEY = 'xcel-prelicensing';
const POLL_MS = 3000;

function retryServerErrors(failureCount: number, error: Error) {
  if (error instanceof XcelApiError && error.status < 500) return false;
  return failureCount < 2;
}

function isActive(run: XcelRun | null | undefined): boolean {
  return Boolean(run && ACTIVE_RUN_STATES.includes(run.state));
}

export function useXcelAccess() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [KEY, 'access', user?.id ?? null],
    queryFn: ({ signal }) => fetchXcelAccess(signal),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
    retry: retryServerErrors,
  });
}

export function useXcelSettings(enabled: boolean) {
  return useQuery({
    queryKey: [KEY, 'settings'],
    queryFn: ({ signal }) => fetchSettings(signal),
    enabled,
    retry: retryServerErrors,
  });
}

export function useXcelStatus(enabled: boolean) {
  return useQuery({
    queryKey: [KEY, 'status'],
    queryFn: ({ signal }) => fetchStatus(signal),
    enabled,
    retry: retryServerErrors,
    refetchInterval: (query) => (query.state.data?.active_runs.length ? POLL_MS : false),
  });
}

export function useXcelRuns(enabled: boolean) {
  return useQuery({
    queryKey: [KEY, 'runs'],
    queryFn: ({ signal }) => fetchRuns(signal),
    enabled,
    retry: retryServerErrors,
    refetchInterval: (query) => (query.state.data?.results.some(isActive) ? POLL_MS : false),
  });
}

export function useXcelRun(runId: number | null) {
  return useQuery({
    queryKey: [KEY, 'run', runId],
    queryFn: ({ signal }) => fetchRun(runId as number, signal),
    enabled: runId !== null,
    retry: retryServerErrors,
    refetchInterval: (query) => (isActive(query.state.data) ? POLL_MS : false),
  });
}

export function useXcelRunActivity(runId: number | null, live: boolean) {
  return useQuery({
    queryKey: [KEY, 'run-activity', runId],
    queryFn: ({ signal }) => fetchRunActivity(runId as number, signal),
    enabled: runId !== null,
    retry: retryServerErrors,
    refetchInterval: live ? POLL_MS : false,
  });
}

/** Settings, probe and run queueing; each refreshes status and the run list. */
export function useXcelOperations() {
  const client = useQueryClient();
  const settle = () => {
    void client.invalidateQueries({ queryKey: [KEY, 'status'] });
    void client.invalidateQueries({ queryKey: [KEY, 'runs'] });
  };
  return {
    saveSettings: useMutation({
      mutationFn: (patch: SettingsPatch) => patchSettings(patch),
      onSuccess: (settings) => {
        client.setQueryData([KEY, 'settings'], settings);
        settle();
      },
    }),
    probe: useMutation({ mutationFn: () => queueProbe(), onSuccess: settle }),
    run: useMutation({ mutationFn: (runType: QueueableRunType) => queueRun(runType), onSuccess: settle }),
  };
}

export function useMappings(filters: MappingFilters, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [KEY, 'mappings', filters],
    queryFn: ({ pageParam, signal }) => fetchMappings(filters, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled,
    retry: retryServerErrors,
  });
}

export function useMappingCounts(filters: MappingFilters, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, 'mapping-counts', filters],
    queryFn: ({ signal }) => fetchMappingCounts(filters, signal),
    enabled,
    retry: retryServerErrors,
  });
}

export function useHierarchyOptions(role: 'leader' | 'md' | 'smd', search: string, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, 'hierarchy-options', role, search],
    queryFn: ({ signal }) => fetchHierarchyOptions(role, search, signal),
    enabled,
    staleTime: 60_000,
    retry: retryServerErrors,
  });
}

export function useMappingDecision() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, decision }: { studentId: number; decision: Decision }) =>
      postDecision(studentId, decision),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: [KEY, 'mappings'] });
      void client.invalidateQueries({ queryKey: [KEY, 'mapping-counts'] });
      void client.invalidateQueries({ queryKey: [KEY, 'status'] });
    },
  });
}

export function useAccountProgress(accountId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, 'account-progress', accountId],
    queryFn: ({ signal }) => fetchAccountProgress(accountId as number, signal),
    enabled: enabled && accountId !== null,
    retry: retryServerErrors,
  });
}

export function useAccountPrograms(accountId: number | null, offset: number, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, 'account-programs', accountId, offset],
    queryFn: ({ signal }) => fetchAccountPrograms(accountId as number, offset, signal),
    enabled: enabled && accountId !== null,
    retry: retryServerErrors,
  });
}

export function useAccountRefresh(accountId: number | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) => requestAccountRefresh(accountId as number, force),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [KEY, 'account-progress', accountId] });
    },
  });
}

/** Re-read an account's local progress once its refresh run has finished. */
export function useInvalidateAccount() {
  const client = useQueryClient();
  return (accountId: number) => {
    void client.invalidateQueries({ queryKey: [KEY, 'account-progress', accountId] });
    void client.invalidateQueries({ queryKey: [KEY, 'account-programs', accountId] });
  };
}
